import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllQuickTapSessions,
  clearQuickTapSessions,
  fetchQuickTapsIntro,
  updateQuickTapsIntro
} from "./../../actions/quickTapsActions";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  ExpandMore,
  Category,
  SubdirectoryArrowRight,
  ViewList,
  ViewModule,
} from "@mui/icons-material";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import QuickTapsFilterHeader from "./QuickTapsFilterHeader";
import QuickTapsTableRow from "./QuickTapsTableRow";
import QuickTapsIntroSection from "./QuickTapsIntroSection";

const accordionSx = {
  '&:before': { display: 'none' },
  boxShadow: 'none',
  border: '1px solid',
  borderColor: 'divider',
  mb: 1,
  '& .MuiAccordionDetails-root': { padding: 0 },
  '& .MuiCollapse-root': { transition: 'none' },
  '& .MuiAccordionSummary-root': { transition: 'none' },
  '& .MuiAccordionSummary-expandIconWrapper': { transition: 'none' }
};

const accordionSummarySx = {
  backgroundColor: '#f0f0f0',
  '&:hover': { backgroundColor: '#e8e8e8' },
  minHeight: '32px !important',
  height: '32px',
  '&.Mui-expanded': {
    minHeight: '32px !important',
    height: '32px !important'
  },
  '& .MuiAccordionSummary-content': {
    margin: '4px 0',
    '&.Mui-expanded': { margin: '4px 0' }
  }
};

const QuickTapsTable = ({ searchText = "" }) => {
  const dispatch = useDispatch();
  const all_quick_tap_sessions = useSelector((state) => state.quick_taps.all_quick_tap_sessions);
  const quick_taps_intro = useSelector((state) =>
    state.general_data.general_data ? state.general_data.general_data.quick_tap_intro : ""
  );
  const loading = useSelector((state) => state.quick_taps.isLoading);
  const [introText, setIntroText] = useState(quick_taps_intro);
  const [expandedSections, setExpandedSections] = useState({
    categoryQuickTaps: true,
    subcategoryQuickTaps: true,
    unparented: true
  });
  const [filters, setFilters] = useState({
    isActive: 'all',
    featured: 'all',
    premium: 'all'
  });
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'list'

  useEffect(() => {
    dispatch(clearQuickTapSessions());
    dispatch(fetchAllQuickTapSessions());
    dispatch(fetchQuickTapsIntro());
  }, [dispatch]);

  useEffect(() => {
    setIntroText(quick_taps_intro);
  }, [quick_taps_intro]);

  const handleIntroChange = (e) => {
    setIntroText(e.target.value);
  };

  const handleSave = () => {
    dispatch(updateQuickTapsIntro(introText));
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const handleFilterChange = (filterType) => (event) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: event.target.value
    }));
  };

  const applyFilters = (sessions) => {
    return sessions.filter(session => {
      if (filters.isActive !== 'all' && String(Number(session.is_active)) !== filters.isActive) {
        return false;
      }
      if (filters.featured !== 'all' && String(Number(session.is_featured)) !== filters.featured) {
        return false;
      }
      if (filters.premium !== 'all' && String(Number(session.is_premium)) !== filters.premium) {
        return false;
      }
      return true;
    });
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const groupSessionsByParent = (sessions) => {
    const groups = {
      categoryQuickTaps: {},
      subcategoryQuickTaps: {},
      unparented: []
    };

    sessions.forEach(session => {
      if (session.category) {
        const categoryId = session.category.category_id;
        if (!groups.categoryQuickTaps[categoryId]) {
          groups.categoryQuickTaps[categoryId] = {
            title: session.category.category_title,
            sessions: []
          };
        }
        groups.categoryQuickTaps[categoryId].sessions.push(session);
      } else if (session.subcategory) {
        const categoryId = session.subcategory.category.category_id;
        if (!groups.subcategoryQuickTaps[categoryId]) {
          groups.subcategoryQuickTaps[categoryId] = {
            title: session.subcategory.category.category_title,
            subcategories: {}
          };
        }
        const subcategoryId = session.subcategory.subcategory_id;
        if (!groups.subcategoryQuickTaps[categoryId].subcategories[subcategoryId]) {
          groups.subcategoryQuickTaps[categoryId].subcategories[subcategoryId] = {
            title: session.subcategory.subcategory_title,
            sessions: []
          };
        }
        groups.subcategoryQuickTaps[categoryId].subcategories[subcategoryId].sessions.push(session);
      } else {
        groups.unparented.push(session);
      }
    });

    return groups;
  };

  const filteredSessions = all_quick_tap_sessions?.filter((session) =>
    (session.title || '').toLowerCase().includes(searchText.toLowerCase())
  )?.sort((a, b) => {
    // Sort by created_at descending (newest first)
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  const filteredAndProcessedSessions = filteredSessions ? applyFilters(filteredSessions) : [];

  if (loading && !all_quick_tap_sessions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const groupedSessions = groupSessionsByParent(filteredAndProcessedSessions || []);

  const getCategoryInfo = (session) => {
    if (session.category?.category_title) {
      return session.category.category_title;
    }
    if (session.subcategory?.category?.category_title) {
      return session.subcategory.subcategory_title ?
        `${session.subcategory.category.category_title} › ${session.subcategory.subcategory_title}` :
        session.subcategory.category.category_title;
    }
    return 'No Category';
  };

  const renderGroupedSection = (sectionKey, title, icon, rows) => (
    <Accordion
      expanded={expandedSections[sectionKey]}
      onChange={handleAccordionChange(sectionKey)}
      slotProps={{ transition: { timeout: 0 } }}
      sx={accordionSx}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={accordionSummarySx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
            {title}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ width: '100%' }}>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <QuickTapsFilterHeader filters={filters} onFilterChange={handleFilterChange} />
              <TableBody>
                {rows}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderFullListTable = () => {
    return (
      <TableContainer component={Paper} sx={{ marginTop: 2 }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <QuickTapsFilterHeader filters={filters} onFilterChange={handleFilterChange} />
          <TableBody>
            {filteredAndProcessedSessions.map((session) => (
              <QuickTapsTableRow
                key={session.id}
                session={session}
                categoryLabel={getCategoryInfo(session)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <QuickTapsIntroSection
        introText={introText}
        onIntroChange={handleIntroChange}
        onSave={handleSave}
      />

      <Box sx={{ py: 2 }}>
        <LoadingBackdrop open={loading && all_quick_tap_sessions !== null} />

        {/* View Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Quick Taps ({filteredAndProcessedSessions?.length || 0})
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                fontSize: '0.8rem'
              }
            }}
          >
            <ToggleButton value="grouped" aria-label="grouped view">
              <ViewModule sx={{ fontSize: 16, mr: 0.5 }} />
              Grouped
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewList sx={{ fontSize: 16, mr: 0.5 }} />
              Full List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Render based on view mode */}
        {viewMode === 'list' ? (
          renderFullListTable()
        ) : (
          <Box>
            {Object.keys(groupedSessions.categoryQuickTaps).length > 0 &&
              renderGroupedSection(
                'categoryQuickTaps',
                `Category Quick Taps (${Object.values(groupedSessions.categoryQuickTaps).reduce((total, g) => total + (g.sessions?.length || 0), 0)})`,
                <Category sx={{ fontSize: 18 }} />,
                Object.entries(groupedSessions.categoryQuickTaps).flatMap(([categoryId, { title: categoryTitle, sessions }]) =>
                  sessions.map((session) => (
                    <QuickTapsTableRow key={session.id} session={session} categoryLabel={categoryTitle} />
                  ))
                )
              )
            }

            {Object.keys(groupedSessions.subcategoryQuickTaps).length > 0 &&
              renderGroupedSection(
                'subcategoryQuickTaps',
                `Subcategory Quick Taps (${Object.values(groupedSessions.subcategoryQuickTaps).reduce((total, cat) => {
                  return total + Object.values(cat.subcategories || {}).reduce((subTotal, sub) => subTotal + (sub.sessions?.length || 0), 0);
                }, 0)})`,
                <SubdirectoryArrowRight sx={{ fontSize: 18 }} />,
                Object.entries(groupedSessions.subcategoryQuickTaps).flatMap(([categoryId, { title: categoryTitle, subcategories }]) =>
                  Object.entries(subcategories).flatMap(([subcategoryId, { title: subcategoryTitle, sessions }]) =>
                    sessions.map((session) => (
                      <QuickTapsTableRow key={session.id} session={session} categoryLabel={`${categoryTitle} › ${subcategoryTitle}`} />
                    ))
                  )
                )
              )
            }

            {groupedSessions.unparented.length > 0 &&
              renderGroupedSection(
                'unparented',
                `Unparented Quick Taps (${groupedSessions.unparented.length})`,
                null,
                groupedSessions.unparented.map((session) => (
                  <QuickTapsTableRow key={session.id} session={session} categoryLabel="No Category" />
                ))
              )
            }
          </Box>
        )}
      </Box>
    </>
  );
};

export default QuickTapsTable;
